import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router";
import { Card, Pill, EmptyState, HostChip, showToast } from "../components/primitives";
import { useRoutine, useUpdateRoutine, useDeleteRoutine } from "../hooks/use-routines";
import { useChatScope } from "../hooks/use-chat-scope";
import { PromoteToPromptModal } from "../components/scope/PromoteToPromptModal";
import { RunNowButton } from "../components/agent-routines/RunNowButton";
import { RoutineRunHistory } from "../components/agent-routines/RoutineRunHistory";
import { formatRelativeTime } from "../lib/v3-columns";

const OUTCOME_PILL: Record<string, "teal" | "amber" | "red"> = {
  success: "teal",
  partial: "amber",
  error: "red",
};

/**
 * Sprint 14 — read/edit view for a single Routine. Header: name + status. Body:
 * cadence, purpose, prompt body, completion criteria, expected outputs. Sidebar:
 * last-run telemetry. Promote button opens the "Promote to shared Prompt" dialog.
 */
export function RoutineView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const routineQuery = useRoutine(id);
  const updateRoutine = useUpdateRoutine();
  const deleteRoutine = useDeleteRoutine();
  const [promoteOpen, setPromoteOpen] = useState(false);

  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [cadence, setCadence] = useState("");
  const [promptBody, setPromptBody] = useState("");
  const [completion, setCompletion] = useState("");
  const [dirty, setDirty] = useState(false);

  const routine = routineQuery.data;

  // Sprint 23.9 (Fix 6 / S23.9.22) — explicit scope; anchor the AI on this routine.
  useChatScope(
    { kind: "workspace", label: routine?.name ?? "This routine" },
    routine ? { kind: "routine", id, name: routine.name } : null,
    [id, routine?.name],
  );

  useEffect(() => {
    if (routine) {
      setName(routine.name);
      setPurpose(routine.purpose);
      setCadence(routine.cadence);
      setPromptBody(routine.prompt_body ?? routine.resolved_prompt_body ?? "");
      setCompletion(routine.completion_criteria ?? "");
      setDirty(false);
    }
  }, [routine?.id, routine?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  if (routineQuery.isLoading) {
    return <Frame><div style={{ color: "var(--color-text-tertiary)", fontSize: 14 }}>Loading routine…</div></Frame>;
  }
  if (routineQuery.error || !routine) {
    return <Frame><EmptyState>Routine not found.</EmptyState></Frame>;
  }

  const usesSharedPrompt = !routine.prompt_body && !!routine.prompt_ref;

  function save() {
    if (!id) return;
    updateRoutine.mutate(
      {
        id,
        name: name.trim(),
        purpose: purpose.trim(),
        cadence: cadence.trim(),
        completion_criteria: completion.trim(),
        // Only push prompt_body edits when the routine carries an inline body.
        ...(usesSharedPrompt ? {} : { prompt_body: promptBody }),
      },
      {
        onSuccess: () => { showToast("Routine saved"); setDirty(false); },
        onError: (err) => showToast(`Couldn't save: ${err instanceof Error ? err.message : "failed"}`, "error", true),
      },
    );
  }

  function toggleEnabled() {
    if (!id) return;
    updateRoutine.mutate({ id, enabled: !routine!.enabled });
  }

  function remove() {
    if (!id) return;
    if (!confirm(`Delete routine "${routine!.name}"? This can't be undone.`)) return;
    deleteRoutine.mutate({ id }, {
      onSuccess: () => { showToast("Routine deleted"); navigate(`/agents/${routine!.node_id}`); },
      onError: (err) => showToast(`Couldn't delete: ${err instanceof Error ? err.message : "failed"}`, "error", true),
    });
  }

  function field<T extends string>(setter: (v: T) => void) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(e.target.value as T);
      setDirty(true);
    };
  }

  return (
    <Frame>
      <button type="button" onClick={() => navigate(`/agents/${routine.node_id}`)} style={backStyle}>
        <i className="ti ti-arrow-left" style={{ fontSize: 13 }} aria-hidden="true" /> Back to agent
      </button>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 24, marginTop: 8 }}>
        <div>
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <i className="ti ti-repeat" style={{ fontSize: 20, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--color-text-primary)", flex: 1 }}>{routine.name}</h2>
              <Pill variant={routine.enabled ? "teal" : "gray"}>{routine.enabled ? "Enabled" : "Disabled"}</Pill>
              {routine.enabled && !routine.archived_at && <RunNowButton routine={routine} variant="primary" />}
              <button type="button" onClick={toggleEnabled} style={smallBtn}>{routine.enabled ? "Disable" : "Enable"}</button>
            </div>

            <label style={labelStyle}>Name</label>
            <input style={inputStyle} value={name} onChange={field(setName)} />

            <label style={labelStyle}>Purpose</label>
            <input style={inputStyle} value={purpose} onChange={field(setPurpose)} />

            <label style={labelStyle}>Cadence <span style={muted}>(opaque to Kuvio — hosts interpret)</span></label>
            <input style={inputStyle} value={cadence} onChange={field(setCadence)} />

            <label style={labelStyle}>
              Prompt {usesSharedPrompt && <span style={muted}>(shared Prompt — edit it in the prompt library)</span>}
            </label>
            <textarea
              style={{ ...inputStyle, minHeight: 180, resize: "vertical", fontFamily: "var(--font-mono, monospace)" }}
              value={promptBody}
              onChange={field(setPromptBody)}
              readOnly={usesSharedPrompt}
            />

            <label style={labelStyle}>Completion criteria</label>
            <input style={inputStyle} value={completion} onChange={field(setCompletion)} placeholder="Optional — what 'done' means for one run" />

            {routine.expected_outputs.length > 0 && (
              <>
                <label style={labelStyle}>Expected outputs</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {routine.expected_outputs.map((o) => <Pill key={o} variant="gray">{o}</Pill>)}
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 20, alignItems: "center" }}>
              <button type="button" data-variant="primary" onClick={save} disabled={!dirty || updateRoutine.isPending} style={{ fontSize: 13, padding: "8px 14px" }}>
                {updateRoutine.isPending ? "Saving…" : "Save"}
              </button>
              {!usesSharedPrompt && (
                <button type="button" onClick={() => setPromoteOpen(true)} style={{ fontSize: 13, padding: "8px 14px" }}>
                  <i className="ti ti-arrow-up-right" style={{ fontSize: 13 }} aria-hidden="true" /> Promote to shared Prompt
                </button>
              )}
              <button type="button" onClick={remove} style={{ fontSize: 13, padding: "8px 14px", color: "var(--c-red-600)", marginLeft: "auto" }}>
                Delete
              </button>
            </div>
          </Card>
        </div>

        <div>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 12 }}>Last run</div>
            {routine.last_run_at ? (
              <div style={{ fontSize: 13, color: "var(--color-text-secondary)", display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {routine.last_run_outcome && <Pill variant={OUTCOME_PILL[routine.last_run_outcome] ?? "gray"}>{routine.last_run_outcome}</Pill>}
                  <span>{formatRelativeTime(routine.last_run_at)}</span>
                  {routine.last_run_via && <HostChip kind={routine.last_run_via} />}
                </div>
                {routine.last_run_by && <div style={muted}>by {routine.last_run_by}</div>}
                {routine.last_run_summary && <div>"{routine.last_run_summary}"</div>}
                {routine.last_run_created_refs.length > 0 && (
                  <div style={muted}>created: {routine.last_run_created_refs.join(", ")}</div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>
                Never run. Hosts execute this routine and call <code>record_routine_run</code> to write back here.
              </div>
            )}
          </Card>

          <div style={{ marginTop: 16 }}>
            <RoutineRunHistory routineId={id!} />
          </div>
        </div>
      </div>

      <PromoteToPromptModal open={promoteOpen} onClose={() => setPromoteOpen(false)} routine={routine} />
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return <div style={{ maxWidth: 1100, margin: "0 auto", fontFamily: "var(--font-sans)" }}>{children}</div>;
}

const backStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontSize: 13,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 0",
  fontFamily: "var(--font-sans)",
};

const smallBtn: CSSProperties = { fontSize: 12, padding: "4px 10px" };

const labelStyle: CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  margin: "14px 0 4px",
};

const inputStyle: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "8px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  boxSizing: "border-box",
};

const muted: CSSProperties = { color: "var(--color-text-tertiary)", fontWeight: 400, fontSize: 11 };
